import { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Select from "react-select";
import Creatable from "react-select/creatable";
import { Button } from "../../common/Button";
import { Formik, Form, FieldArray, ErrorMessage } from "formik";
import * as Yup from "yup";
import DeleteIcon from "@mui/icons-material/Delete";
import { validation, conditionalValidation } from "../../../utils/constants";

const APLRuleModalWindow = ({
  data,
  onShow,
  onHide,
  action,
  existingRuleData = [],
  currentEditingRule = {},
}) => {
  const [btnLoading, setBtnLoading] = useState(false);

  const operatorList = [
    { label: "equal", value: "equal" },
    { label: "not-equal", value: "not-equal" },
    { label: "contains", value: "contains" },
    { label: "does-not-contain", value: "does-not-contain" },
    { label: "contains-only", value: "contains-only" },
    { label: "greater-than", value: "greater-than" },
    { label: "greater-than-or-equal-to", value: "greater-than-or-equal-to" },
    { label: "less-than", value: "less-than" },
    { label: "less-than-or-equal-to", value: "less-than-or-equal-to" },
  ];

  const bracketOptions = [
    { label: "None", value: null },
    { label: "Open (", value: "open" },
    { label: "Close )", value: "close" },
  ];

  const conjunctionOptions = [
    { label: "None", value: null },
    { label: "AND", value: "AND" },
    { label: "OR", value: "OR" },
  ];

  const triggerQuestionOptions = data.map((question) => ({
    label: question?.text || question?.question,
    value: question?.id,
    type: question?.answerType,
    answerOption: question?.answerOption || question?.options || [],
  }));

  const validationSchema = Yup.object().shape({
    conditions: Yup.array()
      .of(
        Yup.object().shape({
          bracketStart: Yup.mixed().nullable(),
          triggerQuestion: Yup.object().nullable().required("Required field"),
          operator: Yup.object().nullable().required("Required field"),
          triggerAnswer: conditionalValidation(
            Yup.string().required("Required field"),
            {
              validation,
              mandatory: true
            }),
          bracketEnd: Yup.mixed().nullable(),
          conjunction: Yup.mixed().nullable(),
        })
      )
      .min(1, "At least one condition is required"),
    targetQuestion: Yup.array()
      .of(
        Yup.object({
          label: conditionalValidation(
            Yup.string(),
            {
              mandatory: true,
              validation
            }),
          value: conditionalValidation(
            Yup.string(),
            {
              mandatory: true,
              validation
            }),
        })
      )
      .min(1, "Required field"),
  });

  const initialValues = {
    conditions:
      action === "UPDATE" && currentEditingRule.conditions
        ? currentEditingRule.conditions.map((cond) => ({
            bracketStart: cond.bracketStart || null,
            triggerQuestion:
              triggerQuestionOptions.find(
                (option) => option.value === cond["trigger-question-id"]
              ) || null,
            operator:
              operatorList.find((option) => option.value === cond.operator) ||
              null,
            triggerAnswer: cond["trigger-answer"] || "",
            bracketEnd: cond.bracketEnd || null,
            conjunction: cond.conjunction || null,
          }))
        : [
            {
              bracketStart: null,
              triggerQuestion: null,
              operator: null,
              triggerAnswer: "",
              bracketEnd: null,
              conjunction: null,
            },
          ],
    targetQuestion:
      action === "UPDATE" && currentEditingRule["target-question-id"]
        ? triggerQuestionOptions.filter((option) =>
            currentEditingRule["target-question-id"].includes(option.value)
          )
        : [],
  };

  const handleSubmit = (values) => {
    setBtnLoading(true);

    const formattedConditions = values.conditions.map((cond) => ({
      bracketStart: cond.bracketStart,
      "trigger-question-id": cond.triggerQuestion?.value,
      operator: cond.operator?.value,
      "trigger-answer": cond.triggerAnswer,
      bracketEnd: cond.bracketEnd,
      conjunction: cond.conjunction,
    }));

    const rulePreview = values.conditions
      .map((cond) => {
        const startBracket = cond.bracketStart === "open" ? "(" : "";
        const endBracket = cond.bracketEnd === "close" ? ")" : "";
        const question = cond.triggerQuestion?.label || "[Question]";
        const op = cond.operator?.label || "[Operator]";
        const ans = cond.triggerAnswer || "[Answer]";
        const conj = cond.conjunction ? ` ${cond.conjunction} ` : "";

        return `${startBracket}${question} ${op} "${ans}"${endBracket}${conj}`;
      })
      .join("")
      .trim();

    const rule = {
      id:
        action === "UPDATE"
          ? currentEditingRule.id
          : existingRuleData.length > 0
          ? existingRuleData[existingRuleData.length - 1]?.id + 1
          : 1,
      conditions: formattedConditions,
      description: rulePreview,
      "target-question-id": values.targetQuestion.map((opt) => opt.value),
    };

    const ruleIndex = existingRuleData.findIndex(
      (existingRule) => existingRule.id === rule.id
    );
    if (ruleIndex > -1) {
      existingRuleData[ruleIndex] = rule;
    } else {
      existingRuleData.push(rule);
    }

    setBtnLoading(false);
    onHide();
  };

  return (
    <Modal
      backdrop="static"
      show={onShow}
      onHide={onHide}
      size="xl"
      centered
      className="custom-dialog"
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title style={{ textTransform: "capitalize" }}>
          Assessment Pathway Logic Rule
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="apl-rule-small">
          <p className="apl-info-text">
            Tip: Craft your logic rules carefully. Use brackets () to group
            conditions, and combine them with AND / OR to define how they
            interact.
          </p>
          <p className="apl-info-text">
            For example: (Question A = "yes" AND Question B = "no") OR Question
            C = "yes". This will trigger the target question to display.
          </p>

          <hr />
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnChange={false}
            validateOnBlur={true}
          >
            {({ values, setFieldValue }) => {
              const rulePreview = values.conditions
                .map((cond) => {
                  const startBracket = cond.bracketStart === "open" ? "(" : "";
                  const endBracket = cond.bracketEnd === "close" ? ")" : "";
                  const question = cond.triggerQuestion?.label || "[Question]";
                  const op = cond.operator?.label || "[Operator]";
                  const ans = cond.triggerAnswer || "[Answer]";
                  const conj = cond.conjunction ? ` ${cond.conjunction} ` : "";

                  return `${startBracket}${question} ${op} "${ans}"${endBracket}${conj}`;
                })
                .join("")
                .trim();
              return (
                <Form>
                  <FieldArray name="conditions">
                    {({ remove, push }) => (
                      <>
                        {values.conditions.map((cond, index) => (
                          <div
                            key={index}
                            className="row mb-3 align-items-center border-bottom pb-2"
                          >
                            <div style={{ width: "130px" }}>
                              <label className="form-label small">
                                Bracket
                              </label>
                              <Select
                                classNamePrefix="react-select"
                                options={bracketOptions}
                                placeholder="Bracket Start"
                                value={bracketOptions.find(
                                  (b) => b.value === cond.bracketStart
                                )}
                                onChange={(opt) =>
                                  setFieldValue(
                                    `conditions.${index}.bracketStart`,
                                    opt?.value || null
                                  )
                                }
                              />
                            </div>
                            <div style={{ width: "260px" }}>
                              <label className="form-label small">
                                Trigger
                              </label>
                              <Select
                                classNamePrefix="react-select"
                                options={triggerQuestionOptions}
                                placeholder="Trigger Question"
                                value={cond.triggerQuestion}
                                onChange={(opt) =>
                                  setFieldValue(
                                    `conditions.${index}.triggerQuestion`,
                                    opt
                                  )
                                }
                              />
                            </div>
                            <div className="col-md-2">
                              <label className="form-label small">
                                Operator
                              </label>

                              <Select
                                classNamePrefix="react-select"
                                options={operatorList}
                                placeholder="Operator"
                                value={cond.operator}
                                onChange={(opt) =>
                                  setFieldValue(
                                    `conditions.${index}.operator`,
                                    opt
                                  )
                                }
                              />
                            </div>
                            <div className="col-md-2">
                              <label className="form-label small">
                                Trigger Answer
                              </label>

                              {cond.triggerQuestion?.type === "freeText" || cond.triggerQuestion?.type === "singleFreeText" ? (
                                <Creatable
                                  classNamePrefix="react-select"
                                  isClearable
                                  placeholder="Answer"
                                  onChange={(opt) =>
                                    setFieldValue(
                                      `conditions.${index}.triggerAnswer`,
                                      opt?.value || ""
                                    )
                                  }
                                  value={{
                                    label: cond.triggerAnswer,
                                    value: cond.triggerAnswer,
                                  }}
                                />
                              ) : (
                                <Select
                                  classNamePrefix="react-select"
                                  options={cond.triggerQuestion?.answerOption?.map(
                                    (option) => ({
                                      label: option?.text,
                                      value: option?.text,
                                    })
                                  )}
                                  placeholder="Select Answer"
                                  value={
                                    cond.triggerAnswer
                                      ? {
                                          label: cond.triggerAnswer,
                                          value: cond.triggerAnswer,
                                        }
                                      : null
                                  }
                                  onChange={(opt) =>
                                    setFieldValue(
                                      `conditions.${index}.triggerAnswer`,
                                      opt?.value
                                    )
                                  }
                                />
                              )}
                            </div>
                            <div style={{ width: "130px" }}>
                              <label className="form-label small">
                                Bracket
                              </label>
                              <Select
                                classNamePrefix="react-select"
                                options={bracketOptions}
                                placeholder="Bracket End"
                                value={bracketOptions.find(
                                  (b) => b.value === cond.bracketEnd
                                )}
                                onChange={(opt) =>
                                  setFieldValue(
                                    `conditions.${index}.bracketEnd`,
                                    opt?.value || null
                                  )
                                }
                              />
                            </div>
                            <div style={{ width: "120px" }}>
                              <label className="form-label small">
                                Conjunction
                              </label>

                              <Select
                                classNamePrefix="react-select"
                                options={conjunctionOptions}
                                placeholder="Conjunction"
                                value={conjunctionOptions.find(
                                  (c) => c.value === cond.conjunction
                                )}
                                onChange={(opt) =>
                                  setFieldValue(
                                    `conditions.${index}.conjunction`,
                                    opt?.value || null
                                  )
                                }
                              />
                            </div>
                            <div
                              className="col-md-1"
                              style={{ marginTop: "50px" }}
                            >
                              <DeleteIcon
                                onClick={() => remove(index)}
                                style={{
                                  cursor: "pointer",
                                  color: "#c00",
                                  marginBottom: "20px",
                                }}
                                titleAccess="Remove"
                              />
                            </div>
                          </div>
                        ))}
                        <div className="d-flex justify-content-center mb-3">
                          <Button
                            variant="secondary"
                            onClick={() =>
                              push({
                                bracketStart: null,
                                triggerQuestion: null,
                                operator: null,
                                triggerAnswer: "",
                                bracketEnd: null,
                                conjunction: null,
                              })
                            }
                          >
                            + Add Condition
                          </Button>
                        </div>
                      </>
                    )}
                  </FieldArray>

                  <div className="d-flex justify-content-center mt-4">
                    <div className="col-md-12">
                      <label className="form-label small">
                        Target Questions
                      </label>
                      <Select
                        classNamePrefix="react-select"
                        isMulti
                        options={triggerQuestionOptions}
                        placeholder="Target Questions"
                        value={values.targetQuestion}
                        onChange={(opts) =>
                          setFieldValue("targetQuestion", opts || [])
                        }
                      />
                      <ErrorMessage
                        name="targetQuestion"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>

                  {rulePreview && (
                    <div
                      style={{
                        background: "#f9f9f9",
                        border: "1px solid #ddd",
                        padding: "10px",
                        borderRadius: "5px",
                        marginTop: "15px",
                        fontStyle: "italic",
                        fontSize: "0.9em",
                      }}
                    >
                      <strong>Rule Preview:</strong> {rulePreview}
                    </div>
                  )}
                  <p
                    className="apl-info-text-alert"
                    style={{ marginTop: "30px" }}
                  >
                    Be mindful — misplaced brackets or incorrect conjunctions
                    may cause unexpected results.
                  </p>

                  <div className="btn-wrap mt-4">
                    <Button onClick={onHide} variant="secondary">
                      Cancel
                    </Button>
                    <Button type="submit" isLoading={btnLoading}>
                      {action === "UPDATE" ? "Update" : "Create"}
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default APLRuleModalWindow;
